/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, Tabs, Form, Input, Select, Upload, Checkbox, InputNumber, message, Space, Tooltip, Popconfirm, Button as AntButton, Row, Col, Typography } from 'antd';
import { UploadOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { Button, Table } from '../../design-system';
import { BUTTON_PRESETS } from '../../design-system/constants';
import { officeService } from '../../services/officeService';
import BankFormModal from './BankFormModal';
import CostRateFormModal from './CostRateFormModal';
import countries from '../../constants/countries';
import logger from '../../utils/logger';



const { TextArea } = Input;
const { Title } = Typography;


const OfficeSettings = () => {
    const navigate = useNavigate();
    const { id: officeId } = useParams();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [office, setOffice] = useState(null);
    const [activeTab, setActiveTab] = useState('information');
    const [banks, setBanks] = useState([]);
    const [bankModalVisible, setBankModalVisible] = useState(false);
    const [editingBank, setEditingBank] = useState(null);
    const [costRateModalVisible, setCostRateModalVisible] = useState(false);
    const [editingCostRate, setEditingCostRate] = useState(null);
    const [currencies, setCurrencies] = useState([]);
    const [removeFiles, setRemoveFiles] = useState({
        removeFWH: false,
        removeFP: false,
        removePT: false,
        removeStarterGuide: false
    });
    const [costRates, setCostRates] = useState([]);

    // File upload states
    const [fileUploads, setFileUploads] = useState({
        flexi_work_hours_file: [],
        flexi_place_file: [],
        part_time_file: [],
        starter_guide: []
    });

    // Helper function to extract filename from file path
    const getFileName = (filePath) => {
        if (!filePath) return '';
        const parts = filePath.split('/');
        return parts[parts.length - 1];
    };

    // Helper function to get S3 URL (assuming uploads/ paths need conversion)
    const getFileUrl = (filePath) => {
        if (!filePath) return '';
        // If already full URL, return as is
        if (filePath.startsWith('http')) return filePath;
        // Otherwise, construct S3 URL (adjust based on your S3 setup)
        return filePath; // Will be handled by backend API
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        loadCurrencies();

        if (officeId) {
            loadOffice();
            loadBanks();
            loadCostRates();
        } else {
            // Set default values for new office
            form.setFieldsValue({
                currency: 'USD', // Default currency
                custom_order: 0
            });
        }
    }, [officeId]);

    const loadCurrencies = async () => {
        try {
            const data = await officeService.getCurrencies();
            setCurrencies(Array.isArray(data) ? data : []);
        } catch (error) {
            logger.error('Error loading currencies:', error);
            message.error('Failed to load currencies');
        }
    };

    const renderCurrencyOptions = () => currencies.map((currency) => {
        const iso = typeof currency === 'string' ? currency : currency.iso;
        const name = typeof currency === 'string' ? '' : currency.name;
        if (!iso) {
            return null;
        }

        const label = name ? `${iso} - ${name}` : iso;
        return (
            <Select.Option key={iso} value={iso} label={label}>
                {label}
            </Select.Option>
        );
    });

    const loadOffice = async () => {
        try {
            const data = await officeService.getOffice(officeId);
            setOffice(data);

            // Set form values
            form.setFieldsValue({
                full_name: data.full_name,
                address: data.address,
                country: data.country,
                currency: data.currency,
                contact_phone: data.contact_phone,
                registration_number: data.registration_number,
                tax_number: data.tax_number,
                work_permit_type: data.work_permit_type,
                custom_personal_info: data.custom_personal_info,
                custom_order: data.custom_order,
                // Widget
                ...data.widget,
                // Office User Info
                ...data.office_user_info,
                // Office Leave
                // Flexi Work Arrangement
                flexi_work_hours_file: data.flexi_work_arrangement?.flexi_work_hours_file,
                flexi_place_file: data.flexi_work_arrangement?.flexi_place_file,
                part_time_file: data.flexi_work_arrangement?.part_time_file,
                info_url: data.flexi_work_arrangement?.info_url,
                tracking_url: data.flexi_work_arrangement?.tracking_url,
                tips_url: data.flexi_work_arrangement?.tips_url,
            });
        } catch (error) {
            message.error('Failed to load office');
        }
    };

    const loadBanks = async () => {
        try {
            const data = await officeService.getBanks(officeId);
            setBanks(Array.isArray(data) ? data : []);
        } catch (error) {
            logger.error('Error loading banks:', error);
        }
    };

    const loadCostRates = async () => {
        try {
            const data = await officeService.getCostRates(officeId);
            logger.debug('Cost rates loaded:', data);
            setCostRates(Array.isArray(data) ? data : []);
        } catch (error) {
            logger.error('Error loading cost rates:', error);
            message.error('Failed to load cost rates');
            setCostRates([]);
        }
    };

    // Format cost rate data for table display
    const formatCostRates = () => {
        if (!costRates || costRates.length === 0) return [];

        return costRates.map(rate => ({
            key: rate.id,
            valid_at: new Date(rate.valid_at).toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
            }),
            monthly: `$${rate.amount ? rate.amount.toLocaleString() : '0'}`,
            hourly: `$${rate.hourly_rate ? rate.hourly_rate.toLocaleString() : '0'}`,
            id: rate.id
        }));
    };

    // File upload handlers
    const beforeUploadFile = (file, fieldName) => {
        const isValidType =
            file.type === 'application/pdf' ||
            file.type === 'application/msword' ||
            file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
            file.type === 'application/vnd.ms-excel' ||
            file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

        if (!isValidType) {
            message.error('You can only upload PDF, Word, or Excel documents!');
            return false;
        }

        const isLt20M = file.size / 1024 / 1024 < 20;
        if (!isLt20M) {
            message.error('File must be smaller than 20MB!');
            return false;
        }

        setFileUploads(prev => ({ ...prev, [fieldName]: [file] }));
        return false; // Prevent automatic upload
    };

    const handleRemoveFile = (fieldName) => {
        setFileUploads(prev => ({ ...prev, [fieldName]: [] }));
    };

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            if (officeId) {
                // Step 1: Handle file uploads and deletions
                const uploadedUrls = {};
                const filesToDelete = [];

                // Upload new files
                for (const [fieldName, files] of Object.entries(fileUploads)) {
                    if (files.length > 0) {
                        try {
                            const uploadedUrl = await officeService.uploadFile(files[0]);
                            uploadedUrls[fieldName] = uploadedUrl;
                            message.success(`${fieldName.replace(/_/g, ' ')} uploaded successfully`);
                        } catch (error) {
                            logger.error(`Error uploading ${fieldName}:`, error);
                            message.error(`Failed to upload ${fieldName.replace(/_/g, ' ')}`);
                            setLoading(false);
                            return;
                        }
                    }
                }

                // Collect files to delete
                if (removeFiles.removeFWH && office?.flexi_work_arrangement?.flexi_work_hours_file) {
                    filesToDelete.push(office.flexi_work_arrangement.flexi_work_hours_file);
                }
                if (removeFiles.removeFP && office?.flexi_work_arrangement?.flexi_place_file) {
                    filesToDelete.push(office.flexi_work_arrangement.flexi_place_file);
                }
                if (removeFiles.removePT && office?.flexi_work_arrangement?.part_time_file) {
                    filesToDelete.push(office.flexi_work_arrangement.part_time_file);
                }
                if (removeFiles.removeStarterGuide && office?.starter_guide) {
                    filesToDelete.push(office.starter_guide);
                }

                // Delete old files from S3
                if (filesToDelete.length > 0) {
                    try {
                        await officeService.deleteFiles(filesToDelete);
                        message.success('Old files deleted successfully');
                    } catch (error) {
                        logger.error('Error deleting files:', error);
                        // Continue anyway - deletion errors shouldn't block update
                    }
                }

                // Step 2: Prepare office data with updated file URLs
                const officeData = {
                    full_name: values.full_name,
                    address: values.address,
                    country: values.country,
                    ...(values.currency ? { currency: values.currency } : {}),
                    contact_phone: values.contact_phone,
                    registration_number: values.registration_number,
                    tax_number: values.tax_number,
                    work_permit_type: values.work_permit_type,
                    custom_personal_info: values.custom_personal_info,
                    custom_order: values.custom_order || 0,
                    // Starter Guide
                    starter_guide: uploadedUrls.starter_guide ||
                                   (removeFiles.removeStarterGuide ? null : office?.starter_guide),
                    // Widget data
                    widget: {
                        holiday_visibility: values.holiday_visibility || false,
                        payslip_visibility: values.payslip_visibility || false,
                        claim_visibility: values.claim_visibility || false,
                        appraisal_visibility: values.appraisal_visibility || false,
                        training_visibility: values.training_visibility || false,
                        job_visibility: values.job_visibility || false,
                        fwa_visibility: values.fwa_visibility || false,
                        qna_visibility: values.qna_visibility || false,
                        feedback_visibility: values.feedback_visibility || false,
                        recognition_visibility: values.recognition_visibility || false,
                        holiday_link: values.holiday_link,
                        payslip_link: values.payslip_link,
                        claim_link: values.claim_link,
                        feedback_link: values.feedback_link,
                        recognition_link: values.recognition_link,
                    },
                    // Office User Info
                    office_user_info: {
                        about_info: values.about_info ?? true,
                        social_media_info: values.social_media_info ?? true,
                        personal_info: values.personal_info ?? true,
                        contact_info: values.contact_info ?? true,
                        marital_info: values.marital_info ?? true,
                        children_info: values.children_info ?? true,
                        employment_info: values.employment_info ?? true,
                        tax_info: values.tax_info ?? true,
                        payment_info: values.payment_info ?? true,
                    },
                    // Flexi Work Arrangement
                    flexi_work_arrangement: {
                        flexi_work_hours_file: uploadedUrls.flexi_work_hours_file ||
                                               (removeFiles.removeFWH ? null : office?.flexi_work_arrangement?.flexi_work_hours_file),
                        flexi_place_file: uploadedUrls.flexi_place_file ||
                                         (removeFiles.removeFP ? null : office?.flexi_work_arrangement?.flexi_place_file),
                        part_time_file: uploadedUrls.part_time_file ||
                                       (removeFiles.removePT ? null : office?.flexi_work_arrangement?.part_time_file),
                        info_url: values.info_url,
                        tracking_url: values.tracking_url,
                        tips_url: values.tips_url,
                    }
                };

                await officeService.updateOffice(officeId, officeData);
                message.success('Office updated successfully');

                // Reset file upload states
                setFileUploads({
                    flexi_work_hours_file: [],
                    flexi_place_file: [],
                    part_time_file: [],
                    starter_guide: []
                });
                setRemoveFiles({
                    removeFWH: false,
                    removeFP: false,
                    removePT: false,
                    removeStarterGuide: false
                });

                // Reload office data to show updated files
                loadOffice();
                    } else {
                // CREATE: Send only basic information
                const officeData = {
                    full_name: values.full_name,
                    address: values.address,
                    country: values.country,
                    currency: values.currency || 'USD',
                    contact_phone: values.contact_phone,
                    registration_number: values.registration_number,
                    tax_number: values.tax_number,
                    work_permit_type: values.work_permit_type,
                    custom_personal_info: values.custom_personal_info,
                    custom_order: values.custom_order || 0,
                };

                const newOffice = await officeService.createOffice(officeData);
                message.success('Office created successfully');
                // Redirect to edit page to configure tabs
                navigate(`/admin/office/${newOffice.id}`);
            }
        } catch (error) {
            message.error('Failed to save office');
            logger.error('Save office error:', error);
        } finally {
            setLoading(false);
        }
    };

    // Bank CRUD handlers
    const handleBankAdd = () => {
        setEditingBank(null);
        setBankModalVisible(true);
    };

    const handleBankEdit = (bank) => {
        setEditingBank(bank);
        setBankModalVisible(true);
    };

    const handleBankDelete = async (bankId) => {
        try {
            await officeService.deleteBank(bankId);
            message.success('Bank account deleted');
            loadBanks();
        } catch (error) {
            message.error('Failed to delete bank account');
        }
    };

    // Cost Rate CRUD handlers
    const handleCostRateAdd = () => {
        setEditingCostRate(null);
        setCostRateModalVisible(true);
    };

    const handleCostRateEdit = (costRate) => {
        setEditingCostRate(costRate);
        setCostRateModalVisible(true);
    };

    const handleCostRateDelete = async (costRateId) => {
        try {
            await officeService.deleteCostRate(costRateId);
            message.success('Cost rate deleted');
            loadCostRates();
        } catch (error) {
            message.error('Failed to delete cost rate');
        }
    };

    // Modal close handlers
    const handleBankModalClose = (refresh = false) => {
        setBankModalVisible(false);
        setEditingBank(null);
        if (refresh) {
            loadBanks();
        }
    };

    // Bank columns
    const bankColumns = [
        { title: 'Bank Name', dataIndex: 'name', key: 'name' },
        { title: 'Account Number', dataIndex: 'account_number', key: 'account_number' },
        { title: 'Account Name', dataIndex: 'account_name', key: 'account_name' },
        { title: 'Branch Name', dataIndex: 'branch_name', key: 'branch_name' },
        { title: 'Branch Code', dataIndex: 'branch_code', key: 'branch_code' },
        { title: 'Currency', dataIndex: 'currency', key: 'currency' },
        {
            title: 'Actions',
            key: 'actions',
            align: 'center',
            render: (_, record) => (
                <Space size="small">
                    <Tooltip title="Edit">
                        <AntButton
                            type="text"
                            icon={<EditOutlined />}
                            onClick={() => handleBankEdit(record)}
                            size="small"
                        />
                    </Tooltip>
                    <Tooltip title="Delete">
                        <Popconfirm
                            title="Delete Bank Account"
                            description="Are you sure you want to delete this bank account?"
                            onConfirm={() => handleBankDelete(record.id)}
                            okText="Yes"
                            cancelText="No"
                        >
                            <AntButton
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                size="small"
                            />
                        </Popconfirm>
                    </Tooltip>
                </Space>
            )
        }
    ];

    // Widget fields - Split into widgets with and without links
    // Widgets WITHOUT link (only visibility toggle)
    const widgetsNoLink = [
        { key: 'appraisal', label: 'Appraisal' },
        { key: 'training', label: 'Training' },
        { key: 'job', label: 'Jobs' },
        { key: 'fwa', label: 'Flexible Work Arrangement' },
        { key: 'qna', label: 'Q & A' }
    ];

    // Widgets WITH link (visibility + URL/Email input)
    const widgetsWithLink = [
        { key: 'holiday', label: 'Holiday' },
        { key: 'payslip', label: 'Payslip' },
        { key: 'claim', label: 'Expense Request' },
        { key: 'feedback', label: 'Send Feedback' },
        { key: 'recognition', label: 'Send Recognition' }
    ];

    // User Info fields (office_user_info table fields)
    const userInfoFields = [
        { key: 'about_info', label: 'About Me' },
        { key: 'social_media_info', label: 'Let\'s Connect' },
        { key: 'personal_info', label: 'Personal Info' },
        { key: 'contact_info', label: 'Contact Information' },
        { key: 'marital_info', label: 'Marital/Family Information' },
        { key: 'children_info', label: 'Children' },
        { key: 'employment_info', label: 'Employment Info' },
        { key: 'tax_info', label: 'Tax Details' },
        { key: 'payment_info', label: 'Payment' }
    ];

    const tabs = [
        {
            key: 'information',
            label: 'Information',
            children: (
                <div style={{ padding: '16px 0' }}>
                    <Row gutter={24}>
                        <Col xs={24} lg={12}>
                            <Form.Item label="Office Name" name="full_name" rules={[{ required: true }]}>
                                <Input placeholder="Office name" maxLength={64} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} lg={12}>
                            <Form.Item label="Country" name="country">
                                <Select placeholder="Select country" allowClear showSearch optionFilterProp="children">
                                    {countries.map(c => <Select.Option key={c.code} value={c.code}>{c.name}</Select.Option>)}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={24}>
                        <Col xs={24}>
                            <Form.Item label="Address" name="address" rules={[{ required: true }]}>
                                <TextArea rows={3} placeholder="Office address" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={24}>
                        <Col xs={24} lg={12}>
                            <Form.Item label="Currency" name="currency">
                                <Select
                                    placeholder="Select currency"
                                    disabled={!!officeId && !!office?.currency}
                                    showSearch
                                    optionFilterProp="label"
                                >
                                    {renderCurrencyOptions()}
                                </Select>
                            </Form.Item>
                            {!!officeId && (
                                <div style={{ color: '#666', marginTop: -16, marginBottom: 16, fontSize: 12 }}>
                                    {office?.currency
                                        ? 'Note: Currency cannot be changed after it is set'
                                        : 'Note: Currency can be set once for this office'}
                                </div>
                            )}
                        </Col>
                        <Col xs={24} lg={12}>
                            <Form.Item label="Contact Phone" name="contact_phone">
                                <Input maxLength={128} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={24}>
                        <Col xs={24} lg={12}>
                            <Form.Item label="Registration Number" name="registration_number">
                                <Input maxLength={32} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} lg={12}>
                            <Form.Item label="Tax Number" name="tax_number">
                                <Input maxLength={128} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={24}>
                        <Col xs={24} lg={12}>
                            <Form.Item label="Work Permit Types (one per line)" name="work_permit_type">
                                <TextArea rows={5} placeholder="Enter work permit types, one per line" />
                            </Form.Item>

                            <Form.Item label="Custom Personal Info Fields (one per line)" name="custom_personal_info">
                                <TextArea rows={5} placeholder="Enter custom fields, one per line" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} lg={12}>
                            <div style={{ marginBottom: 24 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                    <label style={{ fontWeight: 500 }}>Default cost rate</label>
                                    <AntButton
                                        size="small"
                                        disabled={!officeId || !form.getFieldValue('currency')}
                                        onClick={handleCostRateAdd}
                                    >
                                        Add Rate
                                    </AntButton>
                                </div>
                                <Table
                                    size="small"
                                    columns={[
                                        { title: 'Valid At', dataIndex: 'valid_at', key: 'valid_at', width: '20%' },
                                        { title: 'Monthly', dataIndex: 'monthly', key: 'monthly', width: '35%' },
                                        { title: 'Hourly', dataIndex: 'hourly', key: 'hourly', width: '35%' },
                                        {
                                            title: 'Action',
                                            key: 'action',
                                            width: '15%',
                                            align: 'center',
                                            render: (_, record) => (
                                                <Space size="small">
                                                    <Tooltip title="Edit">
                                                        <AntButton
                                                            type="text"
                                                            size="small"
                                                            icon={<EditOutlined />}
                                                            onClick={() => handleCostRateEdit(costRates.find(cr => cr.id === record.id))}
                                                        />
                                                    </Tooltip>
                                                    <Tooltip title="Delete">
                                                        <AntButton
                                                            type="text"
                                                            size="small"
                                                            danger
                                                            icon={<DeleteOutlined />}
                                                            onClick={() => handleCostRateDelete(record.id)}
                                                        />
                                                    </Tooltip>
                                                </Space>
                                            )
                                        }
                                    ]}
                                    dataSource={formatCostRates()}
                                    pagination={false}
                                    locale={{ emptyText: 'No cost rates configured' }}
                                    bordered
                                />
                            </div>
                        </Col>
                    </Row>

                    <Row gutter={24}>
                        <Col xs={24} lg={12}>
                            <Form.Item label="Custom Order" name="custom_order">
                                <InputNumber min={0} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>

                    {/* Starter Guide Section - Only show in Edit mode */}
                    {officeId && (
                        <Row gutter={24}>
                            <Col xs={24}>
                                <div style={{ marginTop: 24 }}>
                                    <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                                        Starter Guide (PDF)
                                    </label>
                                    {office?.starter_guide && !removeFiles.removeStarterGuide && (
                                        <div style={{
                                            backgroundColor: '#f5f5f5',
                                            padding: '8px 12px',
                                            borderRadius: 4,
                                            marginBottom: 8
                                        }}>
                                            <div style={{ marginBottom: 4 }}>
                                                <a
                                                    href={getFileUrl(office.starter_guide)}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{ color: '#1890ff', textDecoration: 'underline' }}
                                                >
                                                    {getFileName(office.starter_guide)}
                                                </a>
                                            </div>
                                            <Checkbox
                                                checked={removeFiles.removeStarterGuide}
                                                onChange={(e) => setRemoveFiles({ ...removeFiles, removeStarterGuide: e.target.checked })}
                                            >
                                                Remove file
                                            </Checkbox>
                                        </div>
                                    )}
                                    <Upload
                                        maxCount={1}
                                        fileList={fileUploads.starter_guide}
                                        beforeUpload={(file) => beforeUploadFile(file, 'starter_guide')}
                                        onRemove={() => handleRemoveFile('starter_guide')}
                                        accept=".pdf"
                                    >
                                        <AntButton icon={<UploadOutlined />}>Choose PDF...</AntButton>
                                    </Upload>
                                    <p style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                                        Max size 20MB, PDF only. This guide will be available to employees.
                                    </p>
                                </div>
                            </Col>
                        </Row>
                    )}
                </div>
            )
        },
        {
            key: 'widget',
            label: 'Widget',
            children: (
                <div style={{ padding: '16px 0' }}>
                    <Row gutter={24}>
                        {/* Left Column: Widgets without links */}
                        <Col xs={24} md={10}>
                            {widgetsNoLink.map(w => (
                                <div key={w.key} style={{ marginBottom: 16 }}>
                                    <Form.Item name={`${w.key}_visibility`} valuePropName="checked" style={{ marginBottom: 0 }}>
                                        <Checkbox>{w.label}</Checkbox>
                                    </Form.Item>
                                </div>
                            ))}
                        </Col>

                        {/* Right Column: Widgets with links */}
                        <Col xs={24} md={14}>
                            {widgetsWithLink.map(w => (
                                <div key={w.key} style={{ marginBottom: 16 }}>
                                    <Row gutter={16} align="middle">
                                        <Col xs={24} sm={10}>
                                            <Form.Item name={`${w.key}_visibility`} valuePropName="checked" style={{ marginBottom: 0 }}>
                                                <Checkbox>{w.label}</Checkbox>
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} sm={14}>
                                            <Form.Item name={`${w.key}_link`} style={{ marginBottom: 0 }}>
                                                <Input placeholder="URL/Email" size="small" addonBefore="URL/Email" />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                </div>
                            ))}
                        </Col>
                    </Row>
                </div>
            )
        },
        {
            key: 'banks',
            label: 'Bank Accounts',
            children: (
                <div style={{ padding: '16px 0' }}>
                    <Button {...BUTTON_PRESETS.ADD} onClick={handleBankAdd} style={{ marginBottom: 16 }}>
                        Add Bank Account
                    </Button>
                    <Table columns={bankColumns} dataSource={banks} rowKey="id" variant="bordered" />
                </div>
            )
        },
        {
            key: 'fwa',
            label: 'Flexi Work Arrangement',
            children: (
                <div style={{ padding: '16px 0' }}>
                    <Row gutter={24}>
                        <Col xs={24} md={8}>
                            <div className="form-group">
                                <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                                    Flexi Time (FT)
                                </label>
                                {office?.flexi_work_arrangement?.flexi_work_hours_file && !removeFiles.removeFWH && (
                                    <div style={{
                                        backgroundColor: '#f5f5f5',
                                        padding: '8px 12px',
                                        borderRadius: 4,
                                        marginBottom: 8
                                    }}>
                                        <div style={{ marginBottom: 4 }}>
                                            <a
                                                href={getFileUrl(office.flexi_work_arrangement.flexi_work_hours_file)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{ color: '#1890ff', textDecoration: 'underline' }}
                                            >
                                                {getFileName(office.flexi_work_arrangement.flexi_work_hours_file)}
                                            </a>
                                        </div>
                                        <Checkbox
                                            checked={removeFiles.removeFWH}
                                            onChange={(e) => setRemoveFiles({ ...removeFiles, removeFWH: e.target.checked })}
                                        >
                                            Remove file
                                        </Checkbox>
                                    </div>
                                )}
                                <Upload
                                    maxCount={1}
                                    fileList={fileUploads.flexi_work_hours_file}
                                    beforeUpload={(file) => beforeUploadFile(file, 'flexi_work_hours_file')}
                                    onRemove={() => handleRemoveFile('flexi_work_hours_file')}
                                    accept=".pdf,.doc,.docx,.xls,.xlsx"
                                >
                                    <AntButton icon={<UploadOutlined />}>Choose file...</AntButton>
                                </Upload>
                                <p style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                                    Max size 20MB, supported: .pdf, .doc, .docx, .xls, .xlsx
                                </p>
                            </div>
                        </Col>
                        <Col xs={24} md={8}>
                            <div className="form-group">
                                <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                                    Flexi Place (FP)
                                </label>
                                {office?.flexi_work_arrangement?.flexi_place_file && !removeFiles.removeFP && (
                                    <div style={{
                                        backgroundColor: '#f5f5f5',
                                        padding: '8px 12px',
                                        borderRadius: 4,
                                        marginBottom: 8
                                    }}>
                                        <div style={{ marginBottom: 4 }}>
                                            <a
                                                href={getFileUrl(office.flexi_work_arrangement.flexi_place_file)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{ color: '#1890ff', textDecoration: 'underline' }}
                                            >
                                                {getFileName(office.flexi_work_arrangement.flexi_place_file)}
                                            </a>
                                        </div>
                                        <Checkbox
                                            checked={removeFiles.removeFP}
                                            onChange={(e) => setRemoveFiles({ ...removeFiles, removeFP: e.target.checked })}
                                        >
                                            Remove file
                                        </Checkbox>
                                    </div>
                                )}
                                <Upload
                                    maxCount={1}
                                    fileList={fileUploads.flexi_place_file}
                                    beforeUpload={(file) => beforeUploadFile(file, 'flexi_place_file')}
                                    onRemove={() => handleRemoveFile('flexi_place_file')}
                                    accept=".pdf,.doc,.docx,.xls,.xlsx"
                                >
                                    <AntButton icon={<UploadOutlined />}>Choose file...</AntButton>
                                </Upload>
                                <p style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                                    Max size 20MB, supported: .pdf, .doc, .docx, .xls, .xlsx
                                </p>
                            </div>
                        </Col>
                        <Col xs={24} md={8}>
                            <div className="form-group">
                                <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                                    Part Time (PT)
                                </label>
                                {office?.flexi_work_arrangement?.part_time_file && !removeFiles.removePT && (
                                    <div style={{
                                        backgroundColor: '#f5f5f5',
                                        padding: '8px 12px',
                                        borderRadius: 4,
                                        marginBottom: 8
                                    }}>
                                        <div style={{ marginBottom: 4 }}>
                                            <a
                                                href={getFileUrl(office.flexi_work_arrangement.part_time_file)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{ color: '#1890ff', textDecoration: 'underline' }}
                                            >
                                                {getFileName(office.flexi_work_arrangement.part_time_file)}
                                            </a>
                                        </div>
                                        <Checkbox
                                            checked={removeFiles.removePT}
                                            onChange={(e) => setRemoveFiles({ ...removeFiles, removePT: e.target.checked })}
                                        >
                                            Remove file
                                        </Checkbox>
                                    </div>
                                )}
                                <Upload
                                    maxCount={1}
                                    fileList={fileUploads.part_time_file}
                                    beforeUpload={(file) => beforeUploadFile(file, 'part_time_file')}
                                    onRemove={() => handleRemoveFile('part_time_file')}
                                    accept=".pdf,.doc,.docx,.xls,.xlsx"
                                >
                                    <AntButton icon={<UploadOutlined />}>Choose file...</AntButton>
                                </Upload>
                                <p style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                                    Max size 20MB, supported: .pdf, .doc, .docx, .xls, .xlsx
                                </p>
                            </div>
                        </Col>
                    </Row>

                    <Row gutter={24}>
                        <Col xs={24} md={8}>
                            <Form.Item label="Info URL" name="info_url">
                                <Input placeholder="https://" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item label="Tracking URL" name="tracking_url">
                                <Input placeholder="https://" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item label="Tips URL" name="tips_url">
                                <Input placeholder="https://" />
                            </Form.Item>
                        </Col>
                    </Row>
                </div>
            )
        },
        {
            key: 'userInfo',
            label: 'User Profile',
            children: (
                <div style={{ padding: '16px 0' }}>
                    <div style={{ marginBottom: 16 }}>
                        <h4 style={{ marginBottom: 8, fontSize: 14, fontWeight: 600 }}>User Profile Page Visibility</h4>
                        <p style={{ marginBottom: 16, color: '#666', fontSize: 13 }}>
                            Select which sections should be visible on employee profile pages
                        </p>
                    </div>
                    <Row gutter={[16, 0]}>
                        {userInfoFields.map(f => (
                            <Col key={f.key} xs={24} sm={12} md={8}>
                                <Form.Item name={f.key} valuePropName="checked" style={{ marginBottom: 16 }}>
                                    <Checkbox>{f.label}</Checkbox>
                                </Form.Item>
                            </Col>
                        ))}
                    </Row>
                </div>
            )
        },
        {
            key: 'leave-settings',
            label: 'Leave Settings',
            children: (
                <div style={{ padding: '16px 0' }}>
                    <Button
                        variant="secondary"
                        onClick={() => navigate(`/admin/hr/leaves?tab=settings&section=leave-types&officeId=${officeId}`)}
                    >
                        Open Leave Settings
                    </Button>
                </div>
            )
        }
    ];

    // NEW OFFICE: Simple form without tabs
    if (!officeId) {
        return (
            <div className="wrapper-grey finance-container">
                <Card>
                    <div className="section-header" style={{ display: 'block' }}>
                        <Title level={4} style={{ margin: 0 }}>ADD OFFICE</Title>
                    </div>

                    <Form form={form} layout="vertical">
                        <Row gutter={24}>
                            <Col xs={24} lg={12}>
                                <Form.Item label="Office Name" name="full_name" rules={[{ required: true, message: 'Please enter office name' }]}>
                                    <Input placeholder="Office name" maxLength={64} />
                                </Form.Item>
                            </Col>

                            <Col xs={24} lg={12}>
                                <Form.Item label="Country" name="country">
                                    <Select placeholder="-- Global / Not set --" allowClear showSearch optionFilterProp="children">
                                        {countries.map(c => <Select.Option key={c.code} value={c.code}>{c.name}</Select.Option>)}
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={24}>
                            <Col xs={24}>
                                <Form.Item label="Company Address" name="address" rules={[{ required: true, message: 'Please enter address' }]}>
                                    <TextArea rows={3} placeholder="Company address" />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={24}>
                            <Col xs={24} lg={12}>
                                <Form.Item label="Currency" name="currency" rules={[{ required: true }]}>
                                    <Select placeholder="Select currency" showSearch optionFilterProp="label">
                                        {renderCurrencyOptions()}
                                    </Select>
                                </Form.Item>
                            </Col>

                            <Col xs={24} lg={12}>
                                <Form.Item label="Contact Phone" name="contact_phone">
                                    <Input maxLength={128} placeholder="Contact phone" />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={24}>
                            <Col xs={24} lg={12}>
                                <Form.Item label="Registration Number" name="registration_number">
                                    <Input maxLength={32} placeholder="Registration number" />
                                </Form.Item>
                            </Col>

                            <Col xs={24} lg={12}>
                                <Form.Item label="Tax Number" name="tax_number">
                                    <Input maxLength={128} placeholder="Tax number" />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={24}>
                            <Col xs={24} lg={12}>
                                <Form.Item
                                    label="Custom working pass for foreign employees (dropdown)"
                                    name="work_permit_type"
                                    help="Separate by new line. Leave blank to use default options."
                                >
                                    <TextArea rows={5} placeholder="Enter work permit types, one per line" />
                                </Form.Item>
                            </Col>

                            <Col xs={24} lg={12}>
                                <Form.Item
                                    label="Custom personal info (input field)"
                                    name="custom_personal_info"
                                    help="Separate by new line."
                                >
                                    <TextArea rows={5} placeholder="Enter custom fields, one per line" />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={24}>
                            <Col xs={24} lg={12}>
                                <Form.Item
                                    label="Custom Order"
                                    name="custom_order"
                                    help="Order for API"
                                >
                                    <InputNumber min={0} style={{ width: '100%' }} />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row>
                            <Col xs={24}>
                                <div style={{ marginTop: 24, display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                                    <Button {...BUTTON_PRESETS.SAVE} onClick={handleSave} loading={loading}>
                                        Save Changes
                                    </Button>
                                    <Button {...BUTTON_PRESETS.CANCEL} onClick={() => navigate('/admin/office')}>
                                        Cancel
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </Form>
                </Card>
            </div>
        );
    }

    // EDIT OFFICE: Full form with tabs
    return (
        <div className="wrapper-grey finance-container">
            <Card>
                <div className="section-header">
                    <Title level={4} style={{ margin: 0 }}>EDIT OFFICE</Title>
                </div>

                <Form form={form} layout="vertical">
                    <Tabs
                        activeKey={activeTab}
                        onChange={setActiveTab}
                        items={tabs.filter(tab => {
                            // Hide FWA tab if fwa_visibility is not enabled
                            if (tab.key === 'fwa') {
                                return form.getFieldValue('fwa_visibility') === true;
                            }
                            return true;
                        })}
                    />

                    {activeTab !== 'leave-settings' && (
                        <div style={{ marginTop: 24, display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                            <Button {...BUTTON_PRESETS.SAVE} onClick={handleSave} loading={loading}>
                                Save Changes
                            </Button>
                            <Button {...BUTTON_PRESETS.CANCEL} onClick={() => navigate('/admin/office')}>
                                Cancel
                            </Button>
                        </div>
                    )}
                </Form>

                {/* Bank Modal */}
                {bankModalVisible && (
                    <BankFormModal
                        visible={bankModalVisible}
                        bank={editingBank}
                        officeId={officeId}
                        currencies={currencies}
                        onClose={handleBankModalClose}
                    />
                )}

                {/* Cost Rate Modal */}
                {costRateModalVisible && (
                    <CostRateFormModal
                        visible={costRateModalVisible}
                        costRate={editingCostRate}
                        officeId={officeId}
                        currency={form.getFieldValue('currency') || 'USD'}
                        onClose={(refresh) => {
                            setCostRateModalVisible(false);
                            if (refresh) loadCostRates();
                        }}
                    />
                )}
            </Card>
        </div>
    );
};

export default OfficeSettings;
